import { ReactNode, useEffect, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import OTPInput from "components/Auth/OTPInput";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import {
  FIRST_CALL_PHONE_HREF,
  FIRST_CALL_PHONE_LABEL,
} from "constants/phones";
import { useEnclosingAuthShell } from "layouts/AuthShellContext";

import { resendLabel } from "./cooldown";
import {
  afterCodeSent,
  CODE_ENDED_RESEND_PROMPT,
  CODE_RESENT_NOTICE,
  resendOnEndedCode,
  type EndedCodeResend,
  type SendTrigger,
  type SubmitOutcome,
} from "./ended-code";
import { useResendCooldown } from "./useResendCooldown";

const schema = z.object({
  code: z.string().length(6, "Please enter the complete verification code"),
});

const TEXT_LINK_CLASS =
  "font-bold text-soil underline underline-offset-4 transition-colors hover:text-soil/70 disabled:pointer-events-none disabled:opacity-50";

type SupportLine = { href: string; label: string };

type OtpVerificationProps = {
  type: "sms" | "email";
  /** The destination as the person should read it: typed, or masked. */
  destinationDisplay: string;
  /** Checks the code. Resolves `code_ended` when the provider ended the
   * code's session: the box then asks `onResend` for a new code itself, once
   * per ended code, and keeps the input open. The screen words no error for
   * it. */
  onSubmit: (code: string) => Promise<SubmitOutcome | void>;
  /** Asks for another code. Resolves true when one was sent: only then does
   * the countdown restart and the send count go up. `code_ended` is the
   * box's own request after a code ended; it is not held back by the
   * countdown, and the screen words a refusal of it as the box expects. */
  onResend?: (trigger: SendTrigger) => Promise<boolean>;
  /** Offered only where the destination may be changed. A screen whose
   * destination is fixed leaves it out. */
  onChangeDestination?: () => void;
  notice?: ReactNode;
  error?: string | null;
  isLoading?: boolean;
  expiryMinutes?: number;
  supportLine?: SupportLine;
  /** Called when the visitor taps the support number. */
  onSupportCall?: () => void;
  /** The send this screen opened on: when it went and how many were sent
   * in a row, so the resend countdown and count carry on across a reload.
   * Read once, when the screen mounts. */
  lastSent?: { sentAt: number; count: number };
};

const DEFAULT_SUPPORT_LINE: SupportLine = {
  href: FIRST_CALL_PHONE_HREF,
  label: FIRST_CALL_PHONE_LABEL,
};

function SupportFooter({
  line,
  type,
  textAlign,
  onCall,
}: {
  line: SupportLine;
  type: "sms" | "email";
  textAlign: string;
  onCall?: () => void;
}) {
  return (
    <p className={`${textAlign} text-sm text-oat-700`}>
      Still no {type === "email" ? "link" : "code"}? Call us at{" "}
      <a
        href={line.href}
        className="font-bold text-soil underline"
        onClick={onCall}
      >
        {line.label}
      </a>
      . We&rsquo;re here 24/7.
    </p>
  );
}

export default function OtpVerification({
  type,
  destinationDisplay,
  onSubmit,
  onResend,
  onChangeDestination,
  notice,
  error,
  isLoading = false,
  expiryMinutes,
  supportLine = DEFAULT_SUPPORT_LINE,
  onSupportCall,
  lastSent,
}: OtpVerificationProps) {
  const [codeComplete, setCodeComplete] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const otpInputRef = useRef<{ focus: () => void } | null>(null);
  const [resending, setResending] = useState(false);
  const [resentNotice, setResentNotice] = useState<string | null>(null);
  const endedResend = useRef<EndedCodeResend>(afterCodeSent());
  const cooldown = useResendCooldown(lastSent);
  const flushLeft = useEnclosingAuthShell() === "quote_flow";
  const textAlign = flushLeft ? "text-left" : "text-center";
  // Flush left, a link's own padding would set its text in from the copy.
  const linkClass = flushLeft ? `${TEXT_LINK_CLASS} px-0` : TEXT_LINK_CLASS;

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  const isSubmitting = methods.formState.isSubmitting || isLoading;

  useEffect(() => {
    setLocalError(error || null);
  }, [error]);

  const requestNewCode = async (trigger: SendTrigger): Promise<boolean> => {
    if (!onResend) return false;
    methods.reset({ code: "" });
    setLocalError(null);
    setResentNotice(null);
    setCodeComplete(false);
    setResending(true);
    let sent = false;
    try {
      sent = await onResend(trigger);
    } finally {
      setResending(false);
    }
    if (sent) {
      endedResend.current = afterCodeSent();
      cooldown.restart();
    }
    setTimeout(() => otpInputRef.current?.focus(), 100);
    return sent;
  };

  const handleCodeEnded = async () => {
    const decision = resendOnEndedCode(endedResend.current);
    endedResend.current = decision.next;
    if (!onResend || !decision.sendNow) {
      setLocalError(CODE_ENDED_RESEND_PROMPT);
      return;
    }
    const sent = await requestNewCode("code_ended");
    if (sent) setResentNotice(CODE_RESENT_NOTICE);
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (data.code && data.code.length === 6) {
      setResentNotice(null);
      const outcome = await onSubmit(data.code);
      if (outcome === "code_ended") await handleCodeEnded();
    }
  });

  const handleComplete = (code: string) => {
    setCodeComplete(true);
    if (!isSubmitting) {
      methods.setValue("code", code);
      handleSubmit();
    }
  };

  const handleChange = (value: string) => {
    setCodeComplete(value.length === 6);
    if (localError) {
      setLocalError(null);
    }
    if (resentNotice) {
      setResentNotice(null);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    await requestNewCode("resend");
  };

  const resendControl = onResend ? (
    <button
      type="button"
      onClick={handleResend}
      disabled={cooldown.secondsLeft > 0 || isSubmitting || resending}
      className={linkClass}
    >
      {resendLabel(
        cooldown.secondsLeft,
        cooldown.sendCount,
        type === "email" ? "link" : "code",
      )}
    </button>
  ) : null;

  const changeControl = onChangeDestination ? (
    <button type="button" onClick={onChangeDestination} className={linkClass}>
      {type === "email"
        ? "Not your email? Go back"
        : "Not your number? Go back"}
    </button>
  ) : null;

  const secondaryActions =
    resendControl || changeControl ? (
      <div
        className={`flex flex-wrap items-center gap-x-6 gap-y-2 text-sm ${
          flushLeft ? "justify-start" : "justify-center"
        }`}
      >
        {resendControl}
        {changeControl}
      </div>
    ) : null;

  if (type === "email") {
    return (
      <div className="space-y-6">
        {notice}
        <div className={textAlign}>
          <p className="text-base text-oat-700">
            We sent a sign-in link to{" "}
            <span className="font-bold text-soil">{destinationDisplay}</span>.
          </p>
          <p className="mt-2 text-base text-oat-700">
            Open it on this device to continue.
          </p>
        </div>
        {error && (
          <div
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-base text-red-800"
          >
            {error}
          </div>
        )}
        {secondaryActions}
        <SupportFooter
          line={supportLine}
          type={type}
          textAlign={textAlign}
          onCall={onSupportCall}
        />
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {notice}
        <div className={textAlign}>
          <p className="text-base text-oat-700">
            We just sent it to{" "}
            <span className="font-bold text-soil">{destinationDisplay}</span>
          </p>
          {expiryMinutes !== undefined && (
            <p className="mt-1 text-sm text-oat-700">
              The code expires in {expiryMinutes} minutes.
            </p>
          )}
        </div>

        <div className={isSubmitting ? "opacity-50" : ""}>
          <OTPInput
            name="code"
            control={methods.control}
            length={6}
            onComplete={handleComplete}
            onChange={handleChange}
            disabled={isSubmitting || resending}
            error={localError}
            align={flushLeft ? "start" : "center"}
            ref={otpInputRef}
          />
        </div>

        {resentNotice && (
          <p role="status" className={`${textAlign} text-sm text-oat-700`}>
            {resentNotice}
          </p>
        )}

        <button
          type="submit"
          className={`${CONTINUE_BUTTON_CLASS} w-full`}
          disabled={isSubmitting || !codeComplete}
        >
          {isSubmitting ? "Verifying..." : "Continue"}
        </button>

        {secondaryActions}
        <SupportFooter
          line={supportLine}
          type={type}
          textAlign={textAlign}
          onCall={onSupportCall}
        />
      </form>
    </FormProvider>
  );
}
